import { useEffect, useState } from 'react'
import { Paperclip, Pencil, Plus, Trash2 } from 'lucide-react'
import Button from '../../components/Button'
import ConfirmDialog from '../../components/ConfirmDialog'
import Drawer from '../../components/Drawer'
import FileField from '../../components/FileField'
import { FormBody, FormCols, FormWell } from '../../components/Form'
import FormRow from '../../components/FormRow'
import IconButton from '../../components/IconButton'
import { EmptyState, LoadingState } from '../../components/States'
import TextField from '../../components/TextField'
import { fileToBase64, extensionOf } from '../../lib/file'
import {
  addCourseAttachment,
  listCourseAttachments,
  removeCourseAttachment,
  updateCourseAttachment,
} from './programsService'
import { strings as S } from './strings'
import './CourseAttachmentsDrawer.css'

const A = S.attachments

const EMPTY = { id: null, name: '', nameEn: '', fileAr: null, fileEn: null }

/* The types the server keeps (CourseAttachmentFiles.AllowedExtensions): documents, pictures
   and recordings. Offering anything else only led to a refusal after the upload. */
const ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.pps,.ppsx,.odt,.ods,.odp,.rtf,.txt,.csv,.png,.jpg,.jpeg,.gif,.webp,.bmp,.mp4,.webm,.mp3,.wav,.m4a,.zip'

/**
 * مرفقات الدورة التدريبية — the legacy `_ProgramManagement_Attachments_Modal`.
 *
 * Four endpoints have been in the service since the slice was wired, reachable from
 * nothing: list, add, update, remove. This is the screen for them.
 *
 * TWO FILES PER ATTACHMENT, one per language, and `AddAttachment` requires BOTH — it
 * refuses a request missing either content or its type. Editing is the softer case: the
 * handler keeps whichever file is not resent, so a rename does not force re-uploading two
 * documents.
 *
 * `FileArabicType` is the EXTENSION, not the MIME type — the stored name is built as
 * `{guid}.{type}` — which is what `extensionOf` produces.
 */
export default function CourseAttachmentsDrawer({ open, course, onClose, onOutcome }) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState(null)
  const [saving, setSaving] = useState(false)
  const [removing, setRemoving] = useState(null)
  const [error, setError] = useState('')

  const courseId = course?.id

  const load = async () => {
    if (!courseId) return
    setLoading(true)
    setRows(await listCourseAttachments(courseId))
    setLoading(false)
  }

  useEffect(() => {
    if (!open) return
    setForm(null)
    setError('')
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, courseId])

  const set = (key) => (value) => {
    setForm((previous) => ({ ...previous, [key]: value }))
    setError('')
  }

  const submit = async () => {
    if (!form.name.trim() || !form.nameEn.trim()) {
      setError(A.nameRequired)
      return
    }
    // Only an ADD needs both files; an edit keeps whatever it is not given.
    if (!form.id && (!form.fileAr || !form.fileEn)) {
      setError(A.filesRequired)
      return
    }

    setSaving(true)
    const payload = {
      id: form.id,
      courseId,
      name: form.name,
      nameEn: form.nameEn,
      fileArabic: await fileToBase64(form.fileAr),
      fileArabicType: extensionOf(form.fileAr),
      fileEnglish: await fileToBase64(form.fileEn),
      fileEnglishType: extensionOf(form.fileEn),
    }

    const outcome = await (form.id ? updateCourseAttachment(payload) : addCourseAttachment(payload))
    setSaving(false)

    if (!outcome.ok) {
      setError(outcome.message || S.actionFailed)
      return
    }
    onOutcome?.(outcome.message)
    setForm(null)
    load()
  }

  return (
    <Drawer
      dialog
      open={open}
      size="lg"
      title={A.title}
      icon={Paperclip}
      subtitle={course?.name}
      onClose={onClose}
    >
      {loading ? (
        <LoadingState inline />
      ) : (
        <div className="cattach">
          <div className="cattach__head">
            <span className="cattach__count">{A.count(rows.length)}</span>
            {!form && (
              <Button onClick={() => setForm(EMPTY)}>
                <Plus size={16} strokeWidth={2} />
                {A.add}
              </Button>
            )}
          </div>

          {form ? (
            <FormWell>
              <FormBody>
                <FormCols>
                  <FormRow label={A.nameAr} error={error && !form.name.trim() ? error : ''}>
                    {(id) => <TextField id={id} value={form.name} onChange={set('name')} />}
                  </FormRow>
                  <FormRow label={A.nameEn}>
                    {(id) => (
                      <TextField id={id} dir="ltr" value={form.nameEn} onChange={set('nameEn')} />
                    )}
                  </FormRow>
                </FormCols>

                <FormCols>
                  {/* On an EDIT the hint says what happens if a slot is left alone, because
                      "no file chosen" reads as "this will be cleared" otherwise. */}
                  <FormRow label={A.fileAr} hint={form.id ? A.keepExisting : undefined}>
                    {(id) => <FileField id={id} accept={ACCEPT} onChange={set('fileAr')} />}
                  </FormRow>
                  <FormRow label={A.fileEn} hint={form.id ? A.keepExisting : undefined}>
                    {(id) => <FileField id={id} accept={ACCEPT} onChange={set('fileEn')} />}
                  </FormRow>
                </FormCols>

                {error && <p className="cattach__error">{error}</p>}

                <div className="cattach__actions">
                  <Button variant="ghost" onClick={() => setForm(null)}>
                    {S.close}
                  </Button>
                  <Button disabled={saving} onClick={submit}>
                    {saving ? A.saving : A.save}
                  </Button>
                </div>
              </FormBody>
            </FormWell>
          ) : rows.length === 0 ? (
            <EmptyState icon={Paperclip} title={A.empty} description={A.emptyHint} />
          ) : (
            <ul className="cattach__list">
              {rows.map((row) => (
                <li key={row.id} className="cattach__row">
                  <span className="cattach__icon" aria-hidden="true">
                    <Paperclip size={16} strokeWidth={1.75} />
                  </span>
                  <span className="cattach__names">
                    <bdi className="cattach__name">{row.name}</bdi>
                    {/* The two stored files, by their download names — an attachment is a
                        PAIR, and a missing half is the thing worth seeing here. */}
                    {/* Links to the two files, not their storage paths printed as text. */}
                    <span className="cattach__files">
                      {row.fileAr || row.fileEn
                        ? [
                            [row.fileAr, A.fileAr],
                            [row.fileEn, A.fileEn],
                          ]
                            .filter(([path]) => path)
                            .map(([path, caption]) => (
                              <a key={path} className="cattach__file" href={`/${path}`} target="_blank" rel="noreferrer">
                                <bdi>{caption}</bdi>
                              </a>
                            ))
                        : A.noFiles}
                    </span>
                  </span>
                  <span className="cattach__row-actions">
                    <IconButton
                      icon={Pencil}
                      label={A.edit}
                      onClick={() =>
                        setForm({ id: row.id, name: row.name, nameEn: row.nameEn, fileAr: null, fileEn: null })
                      }
                    />
                    <IconButton
                      icon={Trash2}
                      label={A.remove}
                      tone="danger"
                      onClick={() => setRemoving(row)}
                    />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <ConfirmDialog
        open={!!removing}
        message={removing ? A.removeConfirm(removing.name) : ''}
        confirmLabel={A.remove}
        cancelLabel={S.close}
        onConfirm={async () => {
          const outcome = await removeCourseAttachment(removing.id)
          setRemoving(null)
          onOutcome?.(outcome.message, !outcome.ok)
          if (outcome.ok) load()
        }}
        onCancel={() => setRemoving(null)}
      />
    </Drawer>
  )
}
